import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Checkbox, CircularProgress, FormControlLabel, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from "@mui/material";

import API from "../../utils/Axios.utils";
import CustomModel from "../../utils/CustomModel";
import { Notification } from "../../utils/Notification";
import logger from "../../utils/logger";
import useDebounce from "../../utils/useDebounce";
import AssignRecruiter from "../../components/JobManagement/AssignRecruiter";

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const SEARCH_DEBOUNCE_MS = 400;

/** "Partially-Fulfilled" -> "Partially Fulfilled" for display. */
const statusLabel = (status) => (status ? String(status).replace(/[-_]/g, " ") : "—");

/**
 * Manager's Job Assignments page (/job-assignments): every job of the tenant
 * with its current recruiter. The manager selects jobs and assigns or
 * reassigns them to a recruiter (bulk). "Unassigned only" lists jobs not yet
 * held by a recruiter.
 */
const JobAssignments = () => {
  const [alert, setAlert] = useState(false);
  const [alertContent, setAlertContent] = useState("");
  const [alertType, setAlertType] = useState("");
  const [jobs, setJobs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [search, setSearch] = useState("");
  const [unassignedOnly, setUnassignedOnly] = useState(true);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/api/job/alljob", {
        params: {
          page: page + 1,
          pageSize,
          ...(debouncedSearch && { search: debouncedSearch }),
          ...(unassignedOnly && { unassigned: true }),
        },
      });
      setJobs(res?.data?.jobPost || []);
      setTotalCount(res?.data?.totalCount || 0);
      logger.debug(`[JobAssignments] Loaded page ${page + 1} (${unassignedOnly ? "unassigned only" : "all jobs"}).`);
    } catch (err) {
      logger.error(`[JobAssignments] Failed to load jobs — ${err?.response?.data?.message || err?.message}`);
      setAlertContent("Could not load jobs. Please try again.");
      setAlertType("error");
      setAlert(true);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, unassignedOnly, setAlert, setAlertContent, setAlertType]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // A new filter or page shows different jobs: start from a clean selection.
  useEffect(() => {
    setSelectedJobs([]);
  }, [page, pageSize, debouncedSearch, unassignedOnly]);

  const assignableIds = jobs.filter((job) => job.can_assign).map((job) => job.job_id);
  const allSelected = assignableIds.length > 0 && assignableIds.every((id) => selectedJobs.includes(id));

  const toggleJob = (jobId) =>
    setSelectedJobs((prev) => (prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]));
  const toggleAll = () => setSelectedJobs(allSelected ? [] : assignableIds);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1.5} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>Job Assignments</Typography>
          <Typography variant="body2" color="text.secondary">
            Assign or reassign jobs to recruiters. A recruiter works on the jobs assigned to them.
          </Typography>
        </Box>
        <Button variant="contained" disabled={!selectedJobs.length} onClick={() => setAssignOpen(true)}>
          Assign recruiter{selectedJobs.length ? ` (${selectedJobs.length})` : ""}
        </Button>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Search by job title or client"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          sx={{ minWidth: 280 }}
        />
        <FormControlLabel
          control={<Checkbox checked={unassignedOnly} onChange={(e) => { setUnassignedOnly(e.target.checked); setPage(0); }} />}
          label="Unassigned only"
        />
      </Stack>

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : jobs.length === 0 ? (
          <Box sx={{ textAlign: "center", p: 6 }}>
            <Typography variant="h6" fontWeight={600}>
              {unassignedOnly ? "Every job has a recruiter" : "No jobs found"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {unassignedOnly ? "Untick \"Unassigned only\" to reassign jobs." : "Try a different search."}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={!allSelected && selectedJobs.length > 0}
                      disabled={!assignableIds.length}
                      onChange={toggleAll}
                      inputProps={{ "aria-label": "Select all jobs on this page" }}
                    />
                  </TableCell>
                  <TableCell>Job code</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Recruiter</TableCell>
                  <TableCell align="right">Aging (days)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.job_id} hover selected={selectedJobs.includes(job.job_id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedJobs.includes(job.job_id)}
                        disabled={!job.can_assign}
                        onChange={() => toggleJob(job.job_id)}
                        inputProps={{ "aria-label": `Select ${job.job_code}` }}
                      />
                    </TableCell>
                    <TableCell>{job.job_code}</TableCell>
                    <TableCell>{job.title}</TableCell>
                    <TableCell>{job.client?.name || "—"}</TableCell>
                    <TableCell>{statusLabel(job.job_status)}</TableCell>
                    <TableCell>{job.recruiter_name || "—"}</TableCell>
                    <TableCell align="right">{job.agingInDays ?? 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      <CustomModel open={assignOpen} handleDialog={() => setAssignOpen(false)} title="Assign Recruiter" width="sm">
        <AssignRecruiter
          jobIds={selectedJobs}
          onSuccess={() => {
            logger.info(`[JobAssignments] Assigned ${selectedJobs.length} job(s).`);
            setAssignOpen(false);
            setSelectedJobs([]);
            loadJobs();
          }}
          setAlert={setAlert}
          setAlertType={setAlertType}
          setAlertContent={setAlertContent}
        />
      </CustomModel>

      <Notification open={alert} onClose={() => setAlert(false)} message={alertContent} severity={alertType} />
    </Box>
  );
};

export default JobAssignments;
